import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';

import * as Sentry from '@sentry/react';

import { Badge, Button, Card, Divider, Drawer, Flex, Group, Stack, Text } from '@mantine/core';

import {
  CUBOH_MENU_CHILD_MAP,
  MENU_OBJECT_TYPES,
  TYPE_TO_READABLE_STRING_MAP,
} from 'constants/menuManagement';

import { copyItemsBetweenMenus, fetchCubohItemChildren, formatNameWithPrice } from 'helpers/menuManagement';
import { formatNotificationTimestamp } from 'helpers/format';
import { useMenuManagement } from 'hooks/useMenuManagement';
import {
  useHandleSuccessNotification,
  useHandleErrorNotification,
} from 'components/MenuManagement/MenuEdit/hooks/useMMNotifications';

import DockedDrawerFormContent from 'primitives/DockedDrawer/DockedDrawerFormContent';
import MenuEdit from '../MenuEdit/MenuEdit';

export default function AddItemDisplayDrawer({
  itemType,
  parentRecord,
  dropdownOptions,
  duplicateChildren,
  opened,
  closeAddItemDisplayDrawer,
  openAddItemFromPosDrawer,
  selectedOptions,
  setSelectedOptions,
}) {
  const [isSaving, setIsSaving] = useState(false);
  const { getSingleCubohMenu, fetchAllOptionsForParentChildSelectors } = useMenuManagement();
  const { menu } = useContext(MenuEdit.Context);
  const handleSuccessNotification = useHandleSuccessNotification();
  const handleErrorNotification = useHandleErrorNotification();

  const handleAddItem = async () => {
    const readableItemType = TYPE_TO_READABLE_STRING_MAP[itemType].toLowerCase();
    setIsSaving(true);

    try {
      await copyItemsBetweenMenus({
        targetParentRecord: parentRecord,
        sourceItemList: selectedOptions.map(selectedItem => selectedItem.uuid),
        duplicateChildren,
        targetItemType: itemType,
      });

      // wait for BE to sync menu before fetching children
      await new Promise(resolve => { setTimeout(resolve, 500); });

      const updatedData = await fetchCubohItemChildren({
        companyID: parentRecord.companyId,
        merchantID: parentRecord.merchantId,
        parentUuid: parentRecord.uuid,
      });

      // only trigger full menu refresh when adding categories
      if (itemType === MENU_OBJECT_TYPES.categories) {
        await getSingleCubohMenu(
          parentRecord.companyId,
          parentRecord.merchantId,
          parentRecord.uuid,
        );
      }

      await fetchAllOptionsForParentChildSelectors({
        uuid: menu?.uuid,
        companyId: menu?.companyId,
        merchantId: menu?.merchantId,
      });

      handleSuccessNotification({
        readableItemType,
        parentUuid: parentRecord?.uuid,
        extraProps: {
          updatedChildren: updatedData.items,
          addedItems: selectedOptions.map(item => item.uuid),
        },
        customMessage: `You have successfully added your ${readableItemType} from POS on ${formatNotificationTimestamp()}.`,
      });
    } catch (e) {
      Sentry.captureException(e);
      handleErrorNotification({
        customMessage: `We could not add your ${readableItemType} from POS. Please contact customer support.`,
      });
    } finally {
      setIsSaving(false);
      setSelectedOptions([]);
      closeAddItemDisplayDrawer();
    }
  };

  const handleCancel = () => {
    setSelectedOptions({});
    closeAddItemDisplayDrawer();
  };

  const buildItemNameComponent = item => {
    const badgeVariant = item.itemType === MENU_OBJECT_TYPES.categories ? 'ink' : 'blue';
    const hasPrice = item?.price || item?.price === 0;

    return (
      <Flex align="center">
        <Badge size="xs" variant={badgeVariant} mr="sm" style={{ flexShrink: 0 }}>
          {TYPE_TO_READABLE_STRING_MAP[item.itemType]}
        </Badge>
        <Text variant="sublabel-sm">{hasPrice ? formatNameWithPrice(item) : item?.name}</Text>
      </Flex>
    );
  };

  const buildAdditionalChildInfo = item => {
    const childType = TYPE_TO_READABLE_STRING_MAP[CUBOH_MENU_CHILD_MAP[item.itemType]];
    if (!childType) return '';
    return `(${item.childCount} ${
      TYPE_TO_READABLE_STRING_MAP[CUBOH_MENU_CHILD_MAP[item.itemType]]
    }${item.childCount !== 1 ? 's' : ''})`;
  };

  const renderButtons = () => (
    <>
      <Button
        variant="outline"
        onClick={() => {
          closeAddItemDisplayDrawer();
          openAddItemFromPosDrawer();
        }}
        disabled={isSaving}
      >
        Back
      </Button>
      <Button
        onClick={handleAddItem}
        loading={isSaving}
        disabled={isSaving}
      >
        Save
      </Button>
    </>
  );

  return (
    <Drawer
      title={`Add ${
        TYPE_TO_READABLE_STRING_MAP[CUBOH_MENU_CHILD_MAP[parentRecord.itemType]]
      } from POS`}
      opened={opened}
      onClose={handleCancel}
      variant="withDock"
    >
      <DockedDrawerFormContent
        dockButtons={renderButtons()}
      >
      <Stack mb="xl">
        <Text>The following will be added to your menu:</Text>
        {dropdownOptions
          .filter(item => selectedOptions?.map(
            selectedItem => selectedItem?.uuid).includes(item?.uuid))
          .map(originalItem => {
            const item = {
              ...originalItem, children: duplicateChildren ? originalItem.children : [],
            };

            return (
              <Card withBorder key={item.uuid}>
                <Stack gap="md">
                  <Group gap="0">
                    {buildItemNameComponent(item)}
                  </Group>
                  <Divider variant="dashed" />
                  {item.children.length ? (
                    item.children.map((child, index) => (
                      <Stack gap="md" key={child.uuid}>
                        <Group gap="0">
                          {buildItemNameComponent(child)}
                          <Text>&nbsp;{buildAdditionalChildInfo(child)}</Text>
                        </Group>
                        {index < item.children.length - 1 && <Divider variant="dashed" />}
                      </Stack>
                    ))
                  ) : (
                    <Text>
                      {`No ${
                        TYPE_TO_READABLE_STRING_MAP[CUBOH_MENU_CHILD_MAP[itemType]] ||
                        'Nested Modifier'
                      }s`}
                    </Text>
                  )}
                </Stack>
              </Card>
            );
          })}
      </Stack>
      </DockedDrawerFormContent>
    </Drawer>
  );
}

AddItemDisplayDrawer.propTypes = {
  itemType: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  parentRecord: PropTypes.object.isRequired,
  opened: PropTypes.bool.isRequired,
  closeAddItemDisplayDrawer: PropTypes.func.isRequired,
  openAddItemFromPosDrawer: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  dropdownOptions: PropTypes.array.isRequired,
  duplicateChildren: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  selectedOptions: PropTypes.array,
  setSelectedOptions: PropTypes.func.isRequired,
};
